import { FC } from 'react'

import styled, { css } from 'styled-components'

import { MAX_MOBILE_WIDTH, MAX_TABLET_WIDTH } from 'src/Styled/variables'

import { StatsSectionInner, StatsSectionInnerProps } from './components'

export interface StatsBlockInnerProps {
  sections: StatsSectionInnerProps[]
  withChangedOrder?: boolean
}

const MOBILE_320 = 320
const MOBILE_390 = 390
const DESKTOP_WIDE = 1280
const THREE = 3
const FOUR = 4

type StatsGridProps = {
  $count: number
  $withChangedOrder?: boolean
}

const reorderFourItems = css<StatsGridProps>`
  ${({ $withChangedOrder, $count }) =>
    $withChangedOrder &&
    $count === FOUR &&
    css`
      & > :nth-child(1) {
        order: 0;
      }
      & > :nth-child(2) {
        order: 2;
      }
      & > :nth-child(3) {
        order: 1;
      }
      & > :nth-child(4) {
        order: 3;
      }
    `}
`

const mobileUpTo320 = css`
  @media (max-width: ${MOBILE_320}px) {
    grid-template-columns: 1fr;
  }
`

const mobile321to390 = css<StatsGridProps>`
  @media (min-width: ${MOBILE_320 + 1}px) and (max-width: ${MOBILE_390}px) {
    grid-template-columns: repeat(2, 1fr);
    ${reorderFourItems}
  }
`

const mobile391to767 = css<{ $count: number }>`
  @media (min-width: ${MOBILE_390 + 1}px) and (max-width: ${MAX_MOBILE_WIDTH}) {
    grid-template-columns: repeat(${({ $count }) => $count}, 1fr);
  }
`

const tabletExactly768 = css`
  @media (min-width: 768px) and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const tabletRange = css<StatsGridProps>`
  @media (min-width: 769px) and (max-width: ${MAX_TABLET_WIDTH}) {
    ${({ $count }) =>
      $count === THREE
        ? css`
            grid-template-columns: repeat(3, 1fr);
          `
        : css`
            grid-template-columns: repeat(2, 1fr);
          `}
    ${reorderFourItems}
  }
`

const desktop1024to1279 = css<StatsGridProps>`
  @media (min-width: ${parseInt(MAX_TABLET_WIDTH, 10) +
    1}px) and (max-width: ${DESKTOP_WIDE - 1}px) {
    grid-template-columns: repeat(2, 1fr);
    ${reorderFourItems}
  }
`

const desktop1280plus = css<{ $count: number }>`
  @media (min-width: ${DESKTOP_WIDE}px) {
    grid-template-columns: repeat(${({ $count }) => $count}, 1fr);
  }
`

const StatsBlockInnerContainer = styled.div<{
  $count: number
  $withChangedOrder?: boolean
}>`
  & * {
    white-space: nowrap;
    word-break: normal;
    overflow-wrap: normal;
    hyphens: none;
  }

  & > * {
    min-width: 0;
  }
  background-color: ${({ theme }) => theme.color.white};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  width: 100%;
  display: grid;
  padding: 24px;
  column-gap: 8px;
  row-gap: 16px;

  ${mobileUpTo320}
  ${mobile321to390}
  ${mobile391to767}
  ${tabletExactly768}
  ${tabletRange}
  ${desktop1024to1279}
  ${desktop1280plus}
`

export const StatsInnerBlock: FC<StatsBlockInnerProps> = ({
  sections,
  withChangedOrder,
}) => {
  const count = sections.length
  return (
    <StatsBlockInnerContainer
      $count={count}
      $withChangedOrder={withChangedOrder}
    >
      {sections.map((section) => (
        <StatsSectionInner key={section.title} {...section} />
      ))}
    </StatsBlockInnerContainer>
  )
}
